import { useNavigation } from '@react-navigation/native';
import { useEffect, useState } from 'react';
import { useJob } from '~/hooks/useJob';
import { useJobSubmit } from '~/hooks/useJobSubmit';
import type { GlassArea } from '~/lib/api';
import type { DriverSide } from '~/lib/handedness';
import { uniqueId } from '~/lib/id';
import type { Photo } from '~/lib/photos';
import { VerdictScreen } from '~/screens/Jobs/VerdictScreen';
import { BrandingScreen } from './BrandingScreen';
import { ReviewScreen } from './ReviewScreen';
import { ShootScreen } from './ShootScreen';
import { VinScanScreen } from './VinScanScreen';
import { WaitingScreen } from './WaitingScreen';
import { WhichGlassScreen } from './WhichGlassScreen';

/**
 * The new-inspection journey.
 *
 * vin → areas → shoot → branding → review → wait → verdict
 *
 * All journey state lives here, so stepping backwards costs nothing — a rescan or an
 * area edit must never discard captured photos, which are the expensive thing to lose.
 * Nothing is uploaded until Review submits, so a dropped connection costs a retry rather
 * than a re-shoot.
 *
 * The VIN leads because it is the one input that unlocks vehicle context — make, model,
 * whether the windscreen carries ADAS — and that context changes the assessment.
 * Capturing it while the assessor is still at the car is free; asking afterwards means a
 * second trip. It remains a STEP, not a gate: skipping degrades enrichment to
 * "insurer_only" and the pipeline still returns a verdict.
 */
type Step = 'vin' | 'areas' | 'shoot' | 'branding' | 'review' | 'wait' | 'verdict';

export function NewInspectionScreen() {
  const nav = useNavigation<{ navigate: (s: string) => void }>();
  const [step, setStep] = useState<Step>('vin');
  const [vin, setVin] = useState<string | undefined>();
  const [side, setSide] = useState<DriverSide | null>(null);
  const [areas, setAreas] = useState<GlassArea[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  /** Quotable before a job id exists. A new one means a genuinely new journey. */
  const [reference, setReference] = useState(() => uniqueId().slice(0, 8).toUpperCase());
  /** Which area Shoot should open at when re-entered from Review. */
  const [shootAt, setShootAt] = useState<GlassArea | null>(null);

  const submitter = useJobSubmit();
  // Polls only once a job exists; stops at a terminal status.
  const { job } = useJob(step === 'wait' && jobId ? jobId : null);

  const addPhoto = (p: Photo) => setPhotos((prev) => [...prev, p]);
  const removePhoto = (id: string) => setPhotos((prev) => prev.filter((p) => p.id !== id));

  const startFresh = () => {
    setVin(undefined);
    setSide(null);
    setAreas([]);
    setPhotos([]);
    setJobId(null);
    setShootAt(null);
    setReference(uniqueId().slice(0, 8).toUpperCase());
    setStep('vin');
  };

  // Terminal status advances on its own — the operator should not have to notice that
  // the job finished. In an effect, not during render: setState while rendering is a
  // React anti-pattern and would warn.
  const terminal = job ? ['complete', 'partial', 'failed'].includes(job.status) : false;
  useEffect(() => {
    if (step === 'wait' && terminal) setStep('verdict');
  }, [step, terminal]);

  const submit = async () => {
    // `side` travels with the submission: the gate already asked the operator
    // which side the driver sits on, and the backend needs that same answer to
    // place damage relative to the driver's viewing area.
    const id = await submitter.submit({ areas, photos, vin, side: side ?? undefined });
    if (id) {
      setJobId(id);
      setStep('wait');
    }
  };

  switch (step) {
    case 'vin':
      return (
        <VinScanScreen
          onConfirm={(scanned) => {
            setVin(scanned);
            setStep('areas');
          }}
          onSkip={() => setStep('areas')}
          // The only way out of a started journey. Without it the operator who tapped
          // "New inspection" by mistake had to walk the whole flow or kill the app —
          // `headerShown` is false on this route, so there is no system back either.
          onClose={() => nav.navigate('Home')}
        />
      );

    case 'areas':
      return (
        <WhichGlassScreen
          reference={reference}
          selected={areas}
          onChange={setAreas}
          side={side}
          onSideChange={setSide}
          onContinue={() => setStep('shoot')}
          onBack={() => setStep('vin')}
        />
      );

    case 'shoot':
      return (
        <ShootScreen
          // Re-entering from Review opens at the area that needs work, then the
          // remounted screen starts its own index from there.
          key={shootAt ?? 'all'}
          areas={shootAt ? [shootAt] : areas}
          photos={photos}
          onAdd={addPhoto}
          onRemove={removePhoto}
          onDone={() => {
            setShootAt(null);
            setStep(shootAt ? 'review' : 'branding');
          }}
          onReview={() => {
            setShootAt(null);
            setStep('review');
          }}
          onBack={() => {
            setShootAt(null);
            setStep(shootAt ? 'review' : 'areas');
          }}
        />
      );

    case 'branding':
      return (
        <BrandingScreen
          /*
            Capturing the stamp ADVANCES. `onAdd` only stored the photo, so an operator
            who took one was left on the branding screen with no way forward — the stamp
            was captured and the journey dead-ended, and the only exit was the skip link,
            which reads like discarding the photo you just took. Skipping already advanced,
            which is why the screen looked like it worked.
          */
          onAdd={(photo) => {
            addPhoto(photo);
            setStep('review');
          }}
          onSkip={() => setStep('review')}
          onBack={() => setStep('shoot')}
        />
      );

    case 'review':
      return (
        <ReviewScreen
          reference={reference}
          vin={vin}
          areas={areas}
          photos={photos}
          busy={submitter.busy}
          error={submitter.error}
          onRemove={removePhoto}
          onShootArea={(area) => {
            setShootAt(area);
            setStep('shoot');
          }}
          onCaptureBranding={() => setStep('branding')}
          onEditVin={() => setStep('vin')}
          onSubmit={() => void submit()}
          onBack={() => setStep('shoot')}
        />
      );

    case 'wait':
      return (
        <WaitingScreen
          reference={reference}
          areaCount={areas.length}
          status={job?.status ?? 'queued'}
          onLeave={() => nav.navigate('Home')}
        />
      );

    default:
      return (
        <VerdictScreen
          jobId={jobId as string}
          backLabel="Start another inspection"
          onBack={startFresh}
        />
      );
  }
}
